/**
 * **The browser tab says which page you are on.**
 *
 * Every route rendered the same `<title>` — `Phony Content Analytics`, straight
 * from `index.html` and never touched again, because a single-page app changes
 * the URL without reloading the document. Three tabs open on Stories, Raw Data
 * and the frozen Tableau screens were three identical tabs, and a browser
 * history entry or a bookmark carried no page name either.
 *
 * ### One map, checked against the app
 *
 * The titles live here rather than in each page, so the set can be asserted
 * complete: `documentTitle.test.ts` reads `App.tsx` and fails when a route has
 * no entry, and cross-checks the eight sidebar labels against this map so the
 * tab and the nav can never disagree about what a screen is called. A per-page
 * `useEffect` would drift silently — this is the same reasoning as the
 * authorization matrix test in `test_authorization.py`.
 *
 * Titles match the page's own heading, not the URL. `/legacy` is **Frozen
 * Tableau** as the sidebar calls it, not `Frozen Tableau Dashboards`, because a
 * tab strip truncates hard and the sidebar's wording is already the short one.
 */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/** Also in `index.html`, which is what a browser shows before React runs. */
export const APP_NAME = 'Phony Content Analytics'

export const ROUTE_TITLES: Record<string, string> = {
  // Behind the login.
  '/stories': 'Stories',
  '/spotlights': 'Spotlights',
  '/channels': 'Channel Overview',
  '/revenue': 'Revenue',
  '/raw-data': 'Raw Data',
  '/connections': 'Connections',
  '/connections/matches': 'Episode Matches',
  '/legacy': 'Frozen Tableau',
  '/admin/users': 'Users',
  '/account': 'Account',
  // The signed-out routes. These are the ones most likely to be open in a
  // second tab while someone reads a set-password email, so naming them is
  // worth more here than anywhere.
  '/login': 'Sign In',
  '/register': 'Register',
  '/forgot-password': 'Reset Your Password',
  '/reset-password': 'Choose a New Password',
  '/set-password': 'Set Your Password',
  '/verify-email': 'Confirm Your Email',
}

/**
 * `Raw Data · Phony Content Analytics`, or the bare app name for a path with no
 * title of its own — `/` is the only such route, and it redirects immediately.
 */
export function titleForPath(pathname: string): string {
  // `/stories/` and `/stories` are the same screen; React Router will match
  // either, so the lookup has to as well.
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  const page = ROUTE_TITLES[path]
  return page ? `${page} · ${APP_NAME}` : APP_NAME
}

/** Called once, in `App`. Runs after paint, which is when the tab updates. */
export function useDocumentTitle(): void {
  const { pathname } = useLocation()
  useEffect(() => {
    document.title = titleForPath(pathname)
  }, [pathname])
}
